/** lag-dropout
 * This widget provides the ability to have a panel 'dropout'/dropdown (ie from a button or input box),
 * this panel is positioned absolutely so as not to interfere with the page DOM (ie it hovers above).
 * Common uses for this widget would be an advanced search panel that drops out of a search box, or
 * where more options/info need to be displayed than available in the UI (or would not be an efficient use of space)
 * such as a list of available reports for some section or contextual help information.
 *
 * The content is defined simply as child elements, there is no specific restriction on content, nore
 * is there any automated content. This widget simply provides the panel, and optionally a backdrop.
 *
 * @property {Boolean} [open=false]         - If the dropout is currently open/visible, by default it is not open.
 * @property {Boolean} [autoClose=false]    - If the dropout should automatically close on mouse click outside its bounds.
 * @property {Boolean} [showCloser=false]   - If the close-panel X should be shown, by default it is hidden.
 * @property {String} [width=null]          - Set a specific width rather than letting it auto size to the containing element,
 *                                            any valid CSS value is acceptable. Default (null) will cause it to fill the container.
 *                                            Use 'auto' to make it size dynamically to its content.
 * @property {String} [alignment=null]      - Set the alignment for the dropout, this is useful when you set a specific width
 *                                            rather than letting it fill the container. Values: 'right', 'left', 'fill'/null.
 *
 * EXAMPLE USAGE:
 *      <lag-dropout>...your content...</lag-dropout>
 */

import { PolymerElement } from '@polymer/polymer/polymer-element.js';
import { defineElement } from '../../utility/defineElement.js';
import { template } from './lag-dropout.html.js';

import '@polymer/iron-icon/iron-icon.js';

defineElement(class extends PolymerElement {

    static get is() { return 'lag-dropout'; }

    static get template() { return template; }

    static get properties() {
        return {
            open: {
                type: Boolean,
                value: false,
                notify: true,
                reflectToAttribute: true // Used in CSS
            },

            autoClose: {
                type: Boolean,
                value: false
            },

            showCloser: {
                type: Boolean,
                value: false,
                reflectToAttribute: true // Used in CSS
            },

            width: {
                type: String,
                value: null,
                observer: '_observeWidth'
            },
            alignment: {
                type: String,
                value: null,
                observer: '_observeAlignment'
            }
        };
    }

    connectedCallback() {
        super.connectedCallback();

        this.$.closer.addEventListener('click', _ => { this.open = false; });

        this._bound_handleWindowMouseDown = this._handleWindowMouseDown.bind(this);
        window.addEventListener('mousedown', this._bound_handleWindowMouseDown);
    }
    disconnectedCallback() {
        window.removeEventListener('mousedown', this._bound_handleWindowMouseDown);
    }

    _observeWidth(width, oldWidth) {
        if (width === void 0 || width === oldWidth)
            return;

        this.$.dropout.style.width = width;
    }

    _observeAlignment(alignment, oldAlignment) {
        if (alignment === void 0 || alignment === oldAlignment)
            return;

        if (alignment === null || alignment === 'fill') {
            this.$.dropout.style.left = 0;
            this.$.dropout.style.right = 0;
        }
        else if (alignment === 'left') {
            this.$.dropout.style.left = 0;
            this.$.dropout.style.right = 'auto';
        }
        else if (alignment === 'right') {
            this.$.dropout.style.left = 'auto';
            this.$.dropout.style.right = 0;
        }
        // else invalid alignment value, ignore.
    }

    _calculateScreenOffset(ele) {
        let offsetLeft = 0, offsetTop = 0;

        while (ele) {
            offsetLeft += ele.offsetLeft || 0;
            offsetTop += ele.offsetTop || 0;

            ele = ele.offsetParent;
        }

        return { offsetLeft, offsetTop };
    }

    _handleWindowMouseDown(ev) {
        // If auto-close is disabled, or dropout is not open, or we're clicking directly on the dropout (this) then no need to auto-close, abort.
        if (!this.autoClose || !this.open || ev.target === this)
            return;

        const
            x = ev.clientX,
            y = ev.clientY,
            offsets = this._calculateScreenOffset(this.$.dropout);

        const
            xMin = offsets.offsetLeft,
            xMax = offsets.offsetLeft + this.$.dropout.clientWidth,
            yMin = offsets.offsetTop,
            yMax = offsets.offsetTop + this.$.dropout.clientHeight;

        const
            xInsideDropout = x >= xMin && x <= xMax,
            yInsideDropout = y >= yMin && y <= yMax;

        if (xInsideDropout && yInsideDropout)
            return; // Mouse is within the dropout boundaries, no need to auto-close, abort.
        else // Mouse IS inside the dropout boundaries so close the dropout.
            this.open = false;
    }
});